"use client";

import type { CmsImage } from "@/composites/landing/types";
import * as styles from "./ResponsiveImage.module.scss";

export interface ResponsiveImageProps {
	desktop?: CmsImage;
	mobile?: CmsImage;
	/** Overrides whatever alt either image carries. */
	alt?: string;
}

/**
 * Single-image responsive media block. A native `<picture>` swaps the source
 * at the md (768px) breakpoint: the desktop asset renders at >=768px, the
 * mobile asset below it. Renders nothing when neither image is supplied.
 *
 * The `<img>` must be a direct child of `<picture>` for `<source>` to apply,
 * so this uses a native `<img>` rather than the DS `Image` (whose `<figure>`
 * wrapper would orphan the `<source>` and break the swap). Layout/cropping is
 * handled by the `responsive-image__img` class.
 *
 * Used by the SocialProof block now that CMS ships dedicated desktop/mobile
 * assets in place of the legacy collage. Either field may be omitted; the
 * one that's present is used for both breakpoints.
 */
export function ResponsiveImage({
	desktop,
	mobile,
	alt
}: ResponsiveImageProps) {
	const primary = mobile ?? desktop;
	if (!primary) return null;

	const resolvedAlt = alt ?? primary.alt ?? "";

	return (
		<picture className={styles["responsive-image"]}>
			{desktop ? (
				<source media="(min-width: 768px)" srcSet={desktop.url} />
			) : null}
			<img
				src={primary.url}
				alt={resolvedAlt}
				className={styles["responsive-image__img"]}
				loading="lazy"
				decoding="async"
			/>
		</picture>
	);
}
